import { LogisticsApiError, failureReasonOf } from '../apis/logisticsErrors.js';
import { MAX_QUANTITY } from './quantity.js';

/** An i18n key and its interpolation values — the sub-line a failed write's
 *  toast (web) or alert (mobile) shows under its action-specific title. */
export interface ILogisticsErrorReason {
  key: string;
  values?: Record<string, string | number>;
}

const reason = (key: string, values?: Record<string, string | number>): ILogisticsErrorReason =>
  values ? { key: `logistics.errors.${key}`, values } : { key: `logistics.errors.${key}` };

/**
 * Why a logistics request failed, in words the user can act on. Anything the
 * server did not explain (a network drop, a 500, a reason this client does not
 * know) is the generic line — never the server's own English message. The
 * switch covers every known reason, so a reason added to the shared list
 * without copy here fails to compile.
 */
export function logisticsErrorReason(error: unknown): ILogisticsErrorReason {
  if (!(error instanceof LogisticsApiError)) return reason('generic');
  const known = failureReasonOf(error.reason);
  if (known === undefined) return reason('generic');
  switch (known) {
    case 'synced-locked':
      return reason('syncedLocked');
    case 'unsynced-rival':
      return error.ref ? reason('unsyncedRival', { ref: error.ref }) : reason('unsyncedRivalNoRef');
    case 'LINE_OPEN_ELSEWHERE':
      return reason('lineOpenElsewhere');
    case 'LAST_LINE':
      return reason('lastLine');
    case 'ORDER_CHANGED':
      return reason('orderChanged');
    // The other join refusals are worded by the booking flow that raises
    // them (its own title says what happened); under any other action they
    // are the generic line.
    case 'DELIVERY_NOT_OPEN':
    case 'UNIT_MISMATCH':
    case 'SOURCES_FULL':
    case 'LINES_FULL':
      return reason('generic');
    case 'status-name-taken':
      return reason('statusNameTaken');
    case 'status-in-use':
      return error.count != null ? reason('statusInUse', { count: error.count }) : reason('statusInUseNoCount');
    case 'delivered-not-last':
      return reason('deliveredNotLast');
    case 'quantity-too-large':
      return reason('quantityTooLarge', { max: MAX_QUANTITY.toLocaleString('en-US') });
    case 'quantity-rounds-to-zero':
      return reason('quantityRoundsToZero');
    case 'date-order':
      return reason('dateOrder');
    case 'date-needs-requested':
      return reason('dateNeedsRequested');
    case 'duplicate-line-id':
      return reason('duplicateLineId');
    case 'waste-exceeds-remaining':
      return reason('wasteExceedsRemaining');
    case 'unit-not-stock-unit':
      return error.itemName ? reason('unitNotStockUnit', { material: error.itemName }) : reason('unitNotStockUnitNoName');
  }
}
